import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import firebaseApp from "@/lib/firebase";
import { SchoolMember } from "@/types/school";
import { listRepository } from "./ListRepository";
import { courseRepository } from "./CourseRepository";

/**
 * Repository class for schoolMembers collection operations
 * Handles CRUD operations for school members in Firestore
 */
export class SchoolMembersRepository {
  private db = getFirestore(firebaseApp);
  private schoolMembers = "schoolMembers" as const;

  // add student to school
  async addStudentToSchool(
    schoolId: string,
    name: string,
    email: string,
    notes: string,
    coursesIds: string[]
  ): Promise<SchoolMember> {
    try {
      const newSchoolMember: Omit<SchoolMember, "id"> = {
        schoolId,
        name,
        email,
        role: "student",
        notes,
        joinedAt: Timestamp.now(),
        status: "active",
      };

      const newMemberRef = await addDoc(
        collection(this.db, this.schoolMembers),
        {
          ...newSchoolMember,
        }
      );

      await listRepository.upsertMemberCoursesList({
        memberId: newMemberRef.id,
        coursesIds,
      });

      const members = await this.getAllSchoolMembers(schoolId);

      const newMember = members.find(
        (member: unknown) =>
          (member as SchoolMember).id === newMemberRef.id &&
          (member as SchoolMember).schoolId === schoolId
      );

      return newMember as SchoolMember;
    } catch (error) {
      console.error("Error adding student to school:", error);
      throw error;
    }
  }

  /**
   * Update an existing school member
   */
  async update(
    schoolMemberWithCoursesIds: Omit<SchoolMember, "id" | "joinedAt"> & {
      id: string;
      coursesIds: string[];
    }
  ): Promise<string> {
    try {
      const {
        coursesIds,
        id: schoolMemberId,
        ...schoolMember
      } = schoolMemberWithCoursesIds;

      const docRef = await this.getById(schoolMemberId);
      if (!docRef) {
        throw new Error(`School member with ID ${schoolMemberId} not found`);
      }

      // Omit coursesIds from docRef for update
      const { coursesIds: _removed, ...docRefWithoutCoursesIds } = docRef;
      const updatedSchoolMember = {
        ...docRefWithoutCoursesIds,
        ...schoolMember,
        lastModified: Timestamp.now(),
      } as SchoolMember;

      await updateDoc(doc(this.db, this.schoolMembers, schoolMemberId), {
        ...updatedSchoolMember,
      });

      await listRepository.upsertMemberCoursesList({
        memberId: schoolMemberId,
        coursesIds,
      });

      return schoolMemberId;
    } catch (error) {
      console.error("Error updating school member:", error);
      throw error;
    }
  }

  /**
   * Delete a school member
   */
  async delete(id: string) {
    try {
      await deleteDoc(doc(this.db, this.schoolMembers, id));
    } catch (error) {
      console.error("Error deleting school member:", error);
      throw error;
    }
  }

  // Get all school members
  async getAllSchoolMembers(schoolId: string): Promise<unknown[]> {
    try {
      const q = query(
        collection(this.db, this.schoolMembers),
        where("schoolId", "==", schoolId)
      );
      const snapshot = await getDocs(q);
      const members: unknown[] = [];
      snapshot.forEach((doc) => {
        members.push({ id: doc.id, ...doc.data() });
      });
      // get each member's courses titles
      for (let i = 0; i < members.length; i++) {
        const member = members[i] as SchoolMember & { courses?: string };
        const coursesIds = await listRepository.getCoursesIdsForSchoolMember(
          member.id
        );
        const courses = await courseRepository.getByIds(coursesIds);

        const coursesTitles = courses.map((course) => course.title);
        member.courses = coursesTitles.join(", ");
      }
      return members;
    } catch (error) {
      console.error("Error fetching school members:", error);
      return [];
    }
  }

  /**
   * Get a school member by ID
   */
  async getById(memberId: string): Promise<
    | (SchoolMember & {
        coursesIds: string[];
      })
    | null
  > {
    const docRef = doc(this.db, this.schoolMembers, memberId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const coursesList = await listRepository.getCoursesIdsForSchoolMember(
      memberId
    );
    const data = docSnap.data() as SchoolMember;
    return {
      ...data,
      id: docSnap.id,
      coursesIds: coursesList,
    };
  }

  async getMemberCoursesIdsPermissions(
    email: string,
    schoolId: string
  ): Promise<string[]> {
    const q = query(
      collection(this.db, this.schoolMembers),
      where("schoolId", "==", schoolId),
      where("email", "==", email)
    );
    const snap = await getDocs(q);
    if (snap.empty) return [];
    const doc = snap.docs[0];
    const memberId = doc.id;
    return listRepository.getCoursesIdsForSchoolMember(memberId);
  }

  // Get user member Id by email and school ID
  async getUserMemberIdByEmail(
    schoolId: string,
    userEmail: string
  ): Promise<string | null> {
    try {
      const q = query(
        collection(this.db, this.schoolMembers),
        where("schoolId", "==", schoolId),
        where("email", "==", userEmail),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        console.log("No matching documents found.");
        return null;
      }
      const result = snapshot.docs[0].id;
      return result;
    } catch (error) {
      console.error("Error fetching user member ID by email:", error);
      return null;
    }
  }

  async canMemberViewCourse(
    memberId: string,
    courseId: string
  ): Promise<boolean> {
    const coursesIds = await listRepository.getCoursesIdsForSchoolMember(
      memberId
    );
    if (!coursesIds.includes(courseId)) {
      return false;
    }

    return true;
  }

  async getMemberIdByEmail(
    schoolId: string,
    email: string
  ): Promise<string | null> {
    const q = query(
      collection(this.db, this.schoolMembers),
      where("schoolId", "==", schoolId),
      where("email", "==", email)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const doc = snap.docs[0];
    return doc.id;
  }
}

// Export singleton instance
export const schoolMembersRepository = new SchoolMembersRepository();
